import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { LinearGradient } from "expo-linear-gradient";
import React, { useState, useEffect, useCallback } from "react";
import { ImageBackground, StyleSheet, View, TouchableOpacity, StatusBar } from "react-native";
import tw from "twrnc";

import {
  DEMO_CATEGORIES,
  DEMO_GUEST_PROGRESS,
  DEMO_USER_STREAK,
  DEMO_USER_XP,
  getDemoCategories,
  getScreenshotPersona,
  getSmokeResultsData,
  isScreenshotMode,
  SMOKE_SCENARIO_OPTIONS,
  type SmokeScenario,
} from "../../../lib/screenshotMode";
import { isMainCategory } from "../../constants/categoryFilters";
import {
  getCategoryName,
  getCategoryDescription,
  isTestamentCategory as isTestamentCategoryById,
  isOldTestament as isOldTestamentById,
  isEarlyChurchHistory,
  isChurchTraditions
} from "../../constants/categoryMapping";
import { DSText } from "../../design-system/components/DSText";
import { useAppTheme } from "../../design-system/ThemeProvider";
import { resolveGroupContextMeta } from "../../features/multi-player/utils/groupMeta";
import { useLanguageStore } from "../../features/quiz-core/languageStore";
import { useUserProfile } from "../../features/quiz-core/useStore";
import { t } from "../../i18n/translations";
import type { RootStackParamList } from '../../navigation/types';
import { useGuestUser } from "../contexts/GuestUserContext";
import {
  getDailyVerse,
  getUserStreak,
  getUserXP,
  hasCompletedDailyVerse,
  completeDailyVerse,
  type DailyVerse,
  type UserStreak,
  type UserXP,
} from "../services/gamificationService";
import { supabase } from "../services/supabaseService";

import CategoryPicker, { Category } from "./CategoryPicker";
import { iqesColors } from "./iqesDesignSystem";
import { iqesSceneAssets } from "./iqesSceneSystem";

/**
 * SoloEntryHeroCard - validated session-entry surface for the home/play screen.
 * Keeps the daily challenge dominant and moves streak/level into supporting status cards.
 */
const SoloEntryHeroCard: React.FC<{
  featuredCategory: Category | null;
  onSelect: (category: Category) => void;
  language: string;
  streakDays: number;
  level: number;
  rankLabel: string;
  badgeLabel: string;
  ctaLabel: string;
  supportingText: string;
}> = ({ featuredCategory, onSelect, language, streakDays, level, rankLabel, badgeLabel, ctaLabel, supportingText }) => {
  if (!featuredCategory) return null;

  return (
    <View style={styles.shellHeaderWrap}>
      <TouchableOpacity
        style={[
          styles.kingdomHeroPressable,
        ]}
        onPress={() => onSelect(featuredCategory)}
        activeOpacity={0.85}
      >
        <ImageBackground
          source={iqesSceneAssets.sunsetCross}
          resizeMode="cover"
          imageStyle={styles.kingdomHeroImage}
          style={styles.kingdomHero}
        >
          <LinearGradient
            colors={['rgba(3,5,16,0.24)', 'rgba(8,10,24,0.82)', 'rgba(18,8,31,0.96)']}
            style={StyleSheet.absoluteFill}
          />
          <View style={styles.heroGlowLarge} />
          <View style={styles.heroTopRow}>
            <View style={styles.kingdomTitleRow}>
              <View style={styles.kingdomIconBadge}>
                <Ionicons name="sparkles" size={18} color={iqesColors.goldSoft} />
              </View>
              <DSText
                variant="label"
                color={iqesColors.goldSoft}
                uppercase
                style={styles.kingdomEyebrow}
              >
                {t('quizFlow.kingdomJourney', language)}
              </DSText>
            </View>
            <View style={styles.heroXpRing}>
              <DSText style={styles.heroXpValue}>{xpInfoSafe(level)}</DSText>
              <DSText style={styles.heroXpLabel}>{language === 'es' ? 'NV' : 'LV'}</DSText>
            </View>
          </View>

          <View style={styles.heroBadgeRow}>
            <View style={styles.heroPill}>
              <DSText variant="label" color={iqesColors.ivory} style={styles.heroPillText}>
                {resumeLabelForHero(badgeLabel)}
              </DSText>
            </View>
            <View style={styles.heroPillGold}>
              <DSText variant="label" color={iqesColors.goldSoft} uppercase style={styles.heroPillText}>
                {featuredCategory.name}
              </DSText>
            </View>
          </View>

          <DSText variant="display" color={iqesColors.ivory} style={styles.heroTitle}>
            {featuredCategory.name}
          </DSText>
          <DSText variant="body" color="rgba(255,248,232,0.86)" style={styles.heroSubtitle}>
            {supportingText}
          </DSText>

          <View style={styles.heroMetaRow}>
            <View style={styles.heroMetaPill}>
              <Ionicons name="flame" size={18} color={iqesColors.goldBright} />
              <DSText variant="label" color={iqesColors.ivory} style={styles.heroMetaText}>
                {t('profile.streak', language) || 'Streak'} {streakDays}
              </DSText>
            </View>
            <View style={styles.heroMetaPill}>
              <Ionicons name="diamond" size={18} color="#D8B4FE" />
              <DSText variant="label" color={iqesColors.ivory} style={styles.heroMetaText}>
                {rankLabel} {level}
              </DSText>
            </View>
          </View>

          <View style={styles.heroActionRow}>
            <View style={styles.heroPrimaryCta}>
              <DSText variant="label" color={iqesColors.navy} style={styles.heroPrimaryCtaText}>
                {ctaLabel}
              </DSText>
              <Ionicons name="arrow-forward" size={17} color={iqesColors.navy} />
            </View>
          </View>
        </ImageBackground>
      </TouchableOpacity>

      <View style={styles.shellSectionTitleRow}>
        <DSText
          variant="label"
          color={iqesColors.goldSoft}
          uppercase
          style={styles.shellSectionTitle}
        >
          {t('explore', language) || 'Explore'}
        </DSText>
        <View style={styles.shellSectionRail} />
      </View>
    </View>
  );
};

const xpInfoSafe = (level: number) => Math.max(1, level);

const translateRankName = (rankName: string, language: string) => {
  const key = rankName.trim().toLowerCase();
  const translated = t(`gamification.${key}`, language);
  return translated === `gamification.${key}` ? rankName : translated;
};

const resumeLabelForHero = (label: string) => {
  if (!label) return '01/01';
  return label.length > 12 ? '01/01' : label;
};

const GuestProgressCard: React.FC<{
  language: string;
  quizzesCompleted: number;
  accuracy: number;
  lastPlayedLabel: string;
}> = ({ language, quizzesCompleted, accuracy, lastPlayedLabel }) => {
  const { colors } = useAppTheme();

  return (
    <View style={tw`px-4 pb-3`}>
      <View
        style={[
          tw`rounded-2xl p-4`,
          {
            backgroundColor: colors.surfaceContainerLow,
            borderWidth: 1,
            borderColor: colors.outlineVariant,
          },
        ]}
      >
        <View style={tw`flex-row items-center justify-between mb-3`}>
          <DSText variant="label" color={colors.primaryDark} uppercase style={tw`font-bold tracking-wide`}>
            {t('local_progress_title', language) || 'Local Progress'}
          </DSText>
          <View style={[tw`rounded-full px-3 py-1`, { backgroundColor: colors.accentContainer }]}>
            <DSText variant="micro" color={colors.onAccentContainer} uppercase style={tw`font-bold tracking-wide`}>
              {t('guestMode.guestBadge', language) || 'Guest'}
            </DSText>
          </View>
        </View>

        <DSText variant="body" color={colors.onSurfaceVariant} style={tw`mb-3`}>
          {t('local_progress_subtitle', language) || 'This progress stays on this device until you sign in.'}
        </DSText>

        <View style={tw`flex-row`}>
          <View style={tw`flex-1 mr-2`}>
            <DSText variant="micro" color={colors.onSurfaceVariant} uppercase style={tw`font-bold tracking-wide mb-1`}>
              {t('quizzes_completed_label', language) || 'Quizzes completed'}
            </DSText>
            <DSText variant="title" color={colors.onSurface} style={tw`font-bold`}>
              {quizzesCompleted}
            </DSText>
          </View>
          <View style={tw`flex-1 mx-2`}>
            <DSText variant="micro" color={colors.onSurfaceVariant} uppercase style={tw`font-bold tracking-wide mb-1`}>
              {t('results.accuracy', language) || 'Accuracy'}
            </DSText>
            <DSText variant="title" color={colors.onSurface} style={tw`font-bold`}>
              {accuracy}%
            </DSText>
          </View>
          <View style={tw`flex-1 ml-2`}>
            <DSText variant="micro" color={colors.onSurfaceVariant} uppercase style={tw`font-bold tracking-wide mb-1`}>
              {t('profile.quizHistory', language) || 'Quiz History'}
            </DSText>
            <DSText variant="caption" color={colors.onSurface} style={tw`font-semibold`}>
              {lastPlayedLabel}
            </DSText>
          </View>
        </View>
      </View>
    </View>
  );
};

const SmokeScenarioPanel: React.FC<{
  onOpenScenario: (scenario: SmokeScenario) => void;
  onOpenLiveLoop: () => void;
  onOpenResults: () => void;
}> = ({ onOpenScenario, onOpenLiveLoop, onOpenResults }) => {
  const { colors } = useAppTheme();
  // Collapsed by default so store screenshots of the Play/home screens are clean: on a
  // tablet this whole panel used to be in frame. Capture opens it through the
  // `smoke-lab-toggle` hit target only when it needs a scenario button.
  const [open, setOpen] = React.useState(false);

  if (!open) {
    return (
      <TouchableOpacity
        testID="smoke-lab-toggle"
        accessibilityLabel="Open smoke lab"
        onPress={() => setOpen(true)}
        style={{ height: 6, marginHorizontal: 16, marginBottom: 6, backgroundColor: 'transparent' }}
      />
    );
  }

  return (
    <View
      style={[
        tw`mx-4 mb-3 rounded-2xl p-4`,
        {
          backgroundColor: colors.surfaceContainerLow,
          borderWidth: 1,
          borderColor: colors.outlineVariant,
        },
      ]}
    >
      <DSText variant="label" color={colors.primaryDark} uppercase style={tw`font-bold tracking-wide mb-3`}>
        Smoke Lab
      </DSText>

      <View style={tw`flex-row flex-wrap`}>
        {SMOKE_SCENARIO_OPTIONS.map((scenario) => (
          <TouchableOpacity
            key={scenario.id}
            testID={`smoke-${scenario.id}`}
            style={[
              tw`rounded-full px-3 py-2 mr-2 mb-2`,
              { backgroundColor: colors.primaryContainer },
            ]}
            onPress={() => onOpenScenario(scenario.id)}
          >
            <DSText variant="caption" color={colors.onPrimaryContainer} style={tw`font-semibold`}>
              {scenario.label}
            </DSText>
          </TouchableOpacity>
        ))}

        <TouchableOpacity
          testID="smoke-live-loop"
          style={[
            tw`rounded-full px-3 py-2 mr-2 mb-2`,
            { backgroundColor: colors.secondaryContainer },
          ]}
          onPress={onOpenLiveLoop}
        >
          <DSText variant="caption" color={colors.onSecondaryContainer} style={tw`font-semibold`}>
            Smoke: Live Loop
          </DSText>
        </TouchableOpacity>

        <TouchableOpacity
          testID="smoke-live-loop-all-formats"
          style={[
            tw`rounded-full px-3 py-2 mr-2 mb-2`,
            { backgroundColor: colors.secondaryContainer },
          ]}
          onPress={() => onOpenScenario('live_loop_all_formats')}
        >
          <DSText variant="caption" color={colors.onSecondaryContainer} style={tw`font-semibold`}>
            Smoke: All Formats Loop
          </DSText>
        </TouchableOpacity>

        <TouchableOpacity
          testID="smoke-results"
          style={[
            tw`rounded-full px-3 py-2 mr-2 mb-2`,
            { backgroundColor: colors.accentContainer },
          ]}
          onPress={onOpenResults}
        >
          <DSText variant="caption" color={colors.onAccentContainer} style={tw`font-semibold`}>
            Smoke: Final Results
          </DSText>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const MultiplayerSetupHeroCard: React.FC<{
  badgeLabel: string;
  title: string;
  subtitle: string;
  groupName: string;
}> = ({ badgeLabel, title, subtitle, groupName }) => {
  const { colors } = useAppTheme();

  return (
    <View style={tw`px-4 pt-3 pb-3`}>
      <View
        style={[
          tw`rounded-2xl p-5`,
          {
            backgroundColor: colors.surfaceContainerLow,
            borderWidth: 1,
            borderColor: colors.outlineVariant,
          },
        ]}
      >
        <View style={tw`flex-row items-start justify-between`}>
          <View style={tw`flex-1 pr-3`}>
            <View
              style={[
                tw`self-start rounded-full px-3 py-1 mb-3`,
                { backgroundColor: colors.primaryContainer },
              ]}
            >
              <DSText variant="micro" color={colors.primary} uppercase style={tw`font-bold tracking-wide`}>
                {badgeLabel}
              </DSText>
            </View>
            <DSText
              variant="display"
              color={colors.onSurface}
              style={[tw`font-black mb-2`, { fontSize: 18, lineHeight: 24, flexShrink: 1 }]}
            >
              {title}
            </DSText>
            <DSText
              variant="body"
              color={colors.onSurfaceVariant}
              style={[tw`mb-3`, { lineHeight: 20, flexShrink: 1 }]}
            >
              {subtitle}
            </DSText>
            <View
              style={[
                tw`self-start rounded-full px-3 py-2`,
                { backgroundColor: colors.surfaceContainerHigh },
              ]}
            >
              <DSText variant="caption" color={colors.onSurface} style={tw`font-semibold`}>
                {groupName}
              </DSText>
            </View>
          </View>
          <View
            style={[
              tw`rounded-2xl p-3 items-center justify-center`,
              { backgroundColor: colors.primaryContainer },
            ]}
          >
            <Ionicons name="people-outline" size={22} color={colors.primary} />
          </View>
        </View>
      </View>
    </View>
  );
};

/**
 * DailyVerseCard - Shown before the first quiz of the day in solo mode.
 * Creates a devotional entry point: "Today's verse" → brief reflection → quiz begins.
 */
const DailyVerseCard: React.FC<{
  language: string;
  userId: string;
}> = ({ language, userId }) => {
  const { colors } = useAppTheme();
  const [verse, setVerse] = useState<DailyVerse | null>(null);
  const [completed, setCompleted] = useState(true); // default hidden until loaded

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!userId) return;
      const [dailyVerse, alreadyDone] = await Promise.all([
        getDailyVerse(language),
        hasCompletedDailyVerse(userId),
      ]);
      if (mounted) {
        setVerse(dailyVerse);
        setCompleted(alreadyDone);
      }
    };
    load();
    return () => { mounted = false; };
  }, [userId, language]);

  const handleDismiss = useCallback(async () => {
    setCompleted(true);
    if (userId) {
      await completeDailyVerse(userId);
    }
  }, [userId]);

  if (completed || !verse) return null;

  return (
    <View style={tw`px-4 pt-2 pb-1`}>
      <View
        style={[
          tw`rounded-2xl overflow-hidden`,
          {
            backgroundColor: colors.accentContainer,
            borderWidth: 1,
            borderColor: 'rgba(196, 168, 91, 0.25)',
            shadowColor: colors.accentDark,
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.08,
            shadowRadius: 6,
            elevation: 3,
          },
        ]}
      >
        <View style={tw`p-4`}>
          <View style={tw`flex-row items-center mb-3`}>
            <View style={[tw`rounded-xl p-2 mr-3`, { backgroundColor: 'rgba(196, 168, 91, 0.12)' }]}>
              <Ionicons name="book-outline" size={20} color={colors.accentDark} />
            </View>
            <DSText
              variant="label"
              color={colors.accentDark}
              uppercase
              style={tw`font-bold tracking-wide`}
            >
              {t('gamification.daily_verse', language)}
            </DSText>
          </View>

          <DSText
            variant="body"
            color={colors.onAccentContainer}
            style={[tw`leading-6 mb-2`, { fontStyle: 'italic' }]}
          >
            &ldquo;{verse.verse_text}&rdquo;
          </DSText>
          <DSText
            variant="label"
            color={colors.onAccentContainer}
            style={tw`font-semibold mb-3`}
          >
            — {verse.verse_reference}
          </DSText>

          <DSText variant="label" color={colors.outline} style={tw`mb-4`}>
            {t('gamification.verse_prompt', language)}
          </DSText>

          <TouchableOpacity
            style={[
              tw`rounded-xl py-2 px-4 self-start`,
              { backgroundColor: 'rgba(196, 168, 91, 0.1)' },
            ]}
            onPress={handleDismiss}
            activeOpacity={0.7}
          >
            <DSText variant="label" color={colors.accentDark} style={tw`font-semibold`}>
              {t('gamification.mark_as_read', language)}
            </DSText>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

// Unified props for Solo, Multiplayer, and Challenge modes
interface UnifiedCategoryScreenProps {
  mode?: 'solo' | 'multiplayer' | 'challenge';
  onCategorySelect?: (category: Category) => void;
  challengeMode?: boolean;
  opponent?: { id: string; name: string }; // For Challenge mode
}

type CategoryScreenNavigationProp = StackNavigationProp<
  RootStackParamList,
  "Category" | "MultiplayerCategory" | "ChallengeCategory"
>;

type CategoryScreenRouteProp = RouteProp<
  RootStackParamList,
  "Category" | "MultiplayerCategory" | "ChallengeCategory"
>;

type CategoryRouteParams = {
  rematch?: boolean;
  oldRoomId?: string;
  familyId?: string;
  familyName?: string;
  friendsGroupId?: string;
  friendsGroupName?: string;
  opponent?: unknown;
};

type RawCategoryRow = {
  category_id: number;
  name?: string;
  description?: string;
  categories?: {
    icon?: string;
  };
};

/**
 * UnifiedCategoryScreen - Consolidated Category Selection
 *
 * This component consolidates Solo Play, Multiplayer, and Challenge Category screens,
 * reducing code duplication by ~85% while maintaining all functionality.
 *
 * Key features:
 * - Mode detection from route name or explicit prop ('solo' | 'multiplayer' | 'challenge')
 * - Shared category loading and filtering logic
 * - Mode-specific navigation handling (Solo/Multiplayer/Challenge flows)
 * - Challenge mode support with opponent parameter
 * - Full internationalization support with encoding fix
 * - Enhanced error handling and fallbacks
 * - Maintains all existing animations and styling
 */
const UnifiedCategoryScreen: React.FC<UnifiedCategoryScreenProps> = ({
  mode: explicitMode,
  onCategorySelect,
  challengeMode,
  opponent
}) => {
  const navigation = useNavigation<CategoryScreenNavigationProp>();
  const route = useRoute<CategoryScreenRouteProp>();
  const { language: userLang } = useLanguageStore();
  const { id: userId } = useUserProfile();
  const { isGuest, guestProgress } = useGuestUser();
  const screenshotMode = isScreenshotMode();
  const screenshotGuestMode = screenshotMode && getScreenshotPersona() === 'guest';
  // Gated on screenshot mode, which is false in every production build — this was
  // hard-coded to false, which silently disabled the whole Smoke Lab and with it the
  // only way to reach a deterministic quiz state on DEVICE. Web capture could route
  // around it via window.__navigationRef; native has no such door, so native
  // screenshots were unreachable until this was restored.
  const showSmokeLab = screenshotMode;

  // Determine mode from route name if not explicitly provided
  const mode = explicitMode ||
    (route.name === 'MultiplayerCategory' ? 'multiplayer' :
     route.name === 'ChallengeCategory' ? 'challenge' : 'solo');

  // Extract route parameters - safely handle union types
  const params = (route.params || {}) as CategoryRouteParams;
  const rematch = params.rematch;
  const oldRoomId = params.oldRoomId;
  const familyId = params.familyId;
  const familyName = params.familyName;
  const friendsGroupId = params.friendsGroupId;
  const friendsGroupName = params.friendsGroupName;
  const routeOpponent = params.opponent;
  const challengeOpponent = opponent || routeOpponent;
  const groupMeta = React.useMemo(
    () =>
      resolveGroupContextMeta(
        {
          familyId,
          familyName,
          friendsGroupId,
          friendsGroupName,
        },
        userLang,
      ),
    [familyId, familyName, friendsGroupId, friendsGroupName, userLang],
  );

  // State management
  const [categories, setCategories] = useState<Category[]>(
    screenshotMode ? getDemoCategories(userLang).map(c => ({ category_id: c.id, name: c.name, icon: c.icon, description: '' })) : []
  );
  const [loading, setLoading] = useState(screenshotMode ? false : true);
  const [streakInfo, setStreakInfo] = useState<UserStreak | null>(screenshotMode ? DEMO_USER_STREAK : null);
  const [xpInfo, setXpInfo] = useState<UserXP | null>(screenshotMode ? DEMO_USER_XP : null);
  const [resumeCategoryId, setResumeCategoryId] = useState<number | null>(
    screenshotMode
      ? (screenshotGuestMode
          ? DEMO_GUEST_PROGRESS.lastCategoryId
          : DEMO_CATEGORIES[0]?.id ?? null)
      : null
  );

  useEffect(() => {
    if (!screenshotMode) return;
    setCategories(getDemoCategories(userLang).map(c => ({ category_id: c.id, name: c.name, icon: c.icon, description: '' })));
  }, [screenshotMode, userLang]);

  // Screenshot mode: signal readiness after first render
  useEffect(() => {
    if (screenshotMode && typeof window !== 'undefined') {
      (window as Window & { __screenshotReady?: string }).__screenshotReady = '01_play';
    }
  }, [screenshotMode]);

  /**
   * Unified category fetching logic
   * Combines the robust logic from CategoryScreen with cleaner error handling
   */
  useEffect(() => {
    if (screenshotMode) return;
    async function fetchCategories() {
      setLoading(true);

      try {
        let { data, error } = await supabase
          .from("category_translations")
          .select(`
            category_id,
            name,
            description,
            language_code,
            categories:category_id (
              icon
            )
          `)
          .eq("language_code", userLang);

        // Fallback to English if none found
        if ((!data || data.length === 0) && userLang !== "en") {
          ({ data, error } = await supabase
            .from("category_translations")
            .select(`
              category_id,
              name,
              description,
              language_code,
              categories:category_id (
                icon
              )
            `)
            .eq("language_code", "en"));
        }

        if (error) {
          console.error(`UnifiedCategoryScreen: Error fetching categories:`, error);
          setCategories(getFallbackCategories());
          setLoading(false);
          return;
        }

        // If no data found, use fallback categories
        if (!data || data.length === 0) {
          setCategories(getFallbackCategories());
          setLoading(false);
          return;
        }

        // Process and order categories
        const processedCategories = processCategories(data);
        setCategories(processedCategories);

      } catch (err) {
        console.error(`UnifiedCategoryScreen: Unexpected error:`, err);
        setCategories(getFallbackCategories());
      }

      setLoading(false);
    }

    fetchCategories();
  }, [userLang]);

  useEffect(() => {
    if (screenshotMode) return;
    let mounted = true;

    const loadProgressContext = async () => {
      try {
        if (isGuest) {
          if (mounted) {
            setResumeCategoryId(guestProgress?.lastCategoryId ?? null);
          }
          return;
        }

        if (!userId) return;

        const [streak, xp, latestSession] = await Promise.all([
          getUserStreak(userId),
          getUserXP(userId),
          supabase
            .from("game_sessions")
            .select("category_id")
            .eq("user_id", userId)
            .order("started_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);
        if (!mounted) return;
        setStreakInfo(streak);
        setXpInfo(xp);
        setResumeCategoryId(latestSession.data?.category_id ?? null);
      } catch {
        // Home still renders without progress context.
      }
    };

    loadProgressContext();
    return () => {
      mounted = false;
    };
  }, [guestProgress?.lastCategoryId, isGuest, screenshotMode, userId]);

  const featuredCategory = React.useMemo(() => {
    if (!categories.length) return null;
    if (resumeCategoryId) {
      const matched = categories.find((category) => category.category_id === resumeCategoryId);
      if (matched) return matched;
    }
    return categories[0];
  }, [categories, resumeCategoryId]);

  const guestAccuracy = React.useMemo(() => {
    if (!guestProgress?.totalQuestions) return 0;
    return Math.round((guestProgress.correctAnswers / guestProgress.totalQuestions) * 100);
  }, [guestProgress?.correctAnswers, guestProgress?.totalQuestions]);

  const guestLastPlayedLabel = React.useMemo(() => {
    if (!guestProgress?.quizzesCompleted || !guestProgress?.lastPlayedAt) {
      return t('profile.noProgressYet', userLang) || 'Finish a quiz to start building your journey history.';
    }
    return new Date(guestProgress.lastPlayedAt).toLocaleDateString();
  }, [guestProgress?.lastPlayedAt, guestProgress?.quizzesCompleted, userLang]);

  /**
   * Get fallback categories with proper encoding fixes
   */
  const getFallbackCategories = (): Category[] => {
    return [
      {
        category_id: 1,
        icon: "📖",
        name: getCategoryName(1, userLang, "General Bible Knowledge"),
        description: getCategoryDescription(1, userLang, "Questions covering various topics from the entire Bible")
      },
      {
        category_id: 2,
        icon: "📜",
        name: getCategoryName(2, userLang, "Old Testament"),
        description: getCategoryDescription(2, userLang, "Stories, characters, and events from the Old Testament")
      },
      {
        category_id: 3,
        icon: "✝️",
        name: getCategoryName(3, userLang, "New Testament"),
        description: getCategoryDescription(3, userLang, "Life of Jesus, apostles, and early Christian church")
      },
      {
        category_id: 4,
        icon: "👥",
        name: getCategoryName(4, userLang, "Bible Characters"),
        description: getCategoryDescription(4, userLang, "Famous people and their stories from the Bible")
      }
    ];
  };

  /**
   * Process raw category data from database
   */
  const processCategories = (data: RawCategoryRow[]): Category[] => {
    // Map DB fields to Category type with encoding fixes
    const cats = data
      .map((cat) => ({
        category_id: cat.category_id,
        icon: cat.categories?.icon || "📚", // fallback icon
        name: getCategoryName(cat.category_id, userLang, cat.name), // FIX ENCODING
        description: getCategoryDescription(cat.category_id, userLang, cat.description), // FIX DESCRIPTIONS
      }))
      .filter((cat) => isMainCategory(cat.category_id));

    // Order categories with Old/New Testament first
    const oldTestamentName = t("old", userLang);
    const newTestamentName = t("new", userLang);

    const oldTestament = cats.find(
      (c) => c.name === oldTestamentName || c.name === "Old Testament",
    );
    const newTestament = cats.find(
      (c) => c.name === newTestamentName || c.name === "New Testament",
    );

    const rest = cats.filter(
      (c) =>
        c.name !== oldTestamentName &&
        c.name !== newTestamentName &&
        c.name !== "Old Testament" &&
        c.name !== "New Testament",
    );

    let orderedCats: Category[] = [];
    if (oldTestament) orderedCats.push(oldTestament);
    if (newTestament) orderedCats.push(newTestament);
    orderedCats = [...orderedCats, ...rest];

    return orderedCats;
  };

  /**
   * Enhanced testament category detection
   * Uses both ID-based and text-based detection for maximum reliability
   */
  const isTestamentCategory = (categoryName: string, categoryId?: number): boolean => {
    // PRIORITY 1: Use the reliable ID-based detection
    if (categoryId && isTestamentCategoryById(categoryId)) {
      return true;
    }

    // PRIORITY 2: Fallback to text-based detection
    const oldTestamentName = t("old", userLang);
    const newTestamentName = t("new", userLang);
    const nameLower = categoryName.toLowerCase();

    if (categoryName === oldTestamentName || categoryName === newTestamentName) {
      return true;
    }

    if (nameLower === oldTestamentName.toLowerCase() ||
        nameLower === newTestamentName.toLowerCase()) {
      return true;
    }

    if (nameLower.includes(oldTestamentName.toLowerCase()) ||
        nameLower.includes(newTestamentName.toLowerCase())) {
      return true;
    }

    if (nameLower.includes("old testament") ||
        nameLower.includes("new testament")) {
      return true;
    }

    const testamentKeywords = [
      'testament', 'testamento', 'завет', 'tipan', 'testamento', 'testament',
      'old', 'ancien', 'antiguo', 'altes', 'antigo', 'ветхий', 'lumang', 'antico', 'ብሉይ', '구약',
      'new', 'nouveau', 'nuevo', 'neues', 'novo', 'новый', 'bagong', 'nuovo', 'አዲስ', '신약'
    ];

    return testamentKeywords.some(keyword => nameLower.includes(keyword.toLowerCase()));
  };

  /**
   * Enhanced Old Testament detection
   */
  const isOldTestament = (categoryName: string, categoryId?: number): boolean => {
    // PRIORITY 1: Use the reliable ID-based detection
    if (categoryId && isTestamentCategoryById(categoryId)) {
      return isOldTestamentById(categoryId);
    }

    // PRIORITY 2: Fallback to text-based detection
    const oldTestamentName = t("old", userLang);
    const nameLower = categoryName.toLowerCase();

    if (categoryName === oldTestamentName ||
        nameLower === oldTestamentName.toLowerCase()) {
      return true;
    }

    if (nameLower.includes(oldTestamentName.toLowerCase())) {
      return true;
    }

    const oldKeywords = [
      'old', 'ancien', 'antiguo', 'altes', 'antigo', 'ветхий', 'lumang', 'antico', 'ብሉይ', '구약'
    ];
    return oldKeywords.some(keyword => nameLower.includes(keyword.toLowerCase()));
  };

  /**
   * Handle category selection with mode-specific navigation
   */
  const handleCategorySelect = (item: Category) => {
    // Allow custom category selection handler (for special use cases)
    if (onCategorySelect) {
      onCategorySelect(item);
      return;
    }

    // Check if this is a testament category or church history category
    if (isTestamentCategory(item.name, item.category_id)) {
      // Determine testament type or church history type
      let testament: 'old' | 'new' | 'early_church' | 'church_traditions';

      if (isOldTestament(item.name, item.category_id)) {
        testament = "old";
      } else if (isEarlyChurchHistory(item.category_id)) {
        testament = "early_church";
      } else if (isChurchTraditions(item.category_id)) {
        testament = "church_traditions";
      } else {
        testament = "new";
      }

      // Navigate based on mode
      if (mode === 'multiplayer') {
        navigation.navigate("MultiplayerTestament", {
          category: item.name,
          categoryId: item.category_id,
          initialTestament: testament,
          initialStep: "chooseMode",
          rematch,
          oldRoomId,
          familyId,
          familyName,
          friendsGroupId,
          friendsGroupName,
        });
      } else if (mode === 'challenge') {
        navigation.navigate("ChallengeTestament", {
          opponent: challengeOpponent,
          categoryId: item.category_id,
          category: item.name,
          testament,
        });
      } else {
        navigation.navigate("Testament", {
          category: item.name,
          categoryId: item.category_id,
          initialTestament: testament,
          initialStep: "chooseMode",
          challengeMode,
        });
      }
    } else {
      // Navigate directly to difficulty for non-testament categories
      if (mode === 'multiplayer') {
        navigation.navigate("MultiplayerDifficulty", {
          category: item.name,
          categoryId: item.category_id,
          rematch,
          oldRoomId,
          familyId,
          familyName,
          friendsGroupId,
          friendsGroupName,
        });
      } else if (mode === 'challenge') {
        navigation.navigate("ChallengeDifficulty", {
          opponent: challengeOpponent,
          categoryId: item.category_id,
          category: item.name,
          testament: null, // No testament for non-testament categories
        });
      } else {
        navigation.navigate("Difficulty", {
          category: item.name,
          categoryId: item.category_id,
          challengeMode,
        });
      }
    }
  };

  const handleOpenSmokeScenario = useCallback((scenario: SmokeScenario) => {
    navigation.navigate("SinglePlayerGame", {
      category: "Gospels",
      categoryId: 3,
      difficulty_id: 2,
      questionCount: 1,
      smokeScenario: scenario,
    });
  }, [navigation]);

  const handleOpenSmokeLiveLoop = useCallback(() => {
    navigation.navigate("SinglePlayerGame", {
      category: "Gospels",
      categoryId: 3,
      difficulty_id: 2,
      questionCount: 10,
      smokeScenario: 'live_loop_auto',
    });
  }, [navigation]);

  const handleOpenSmokeResults = useCallback(() => {
    navigation.navigate("SinglePlayerResultsScreen", {
      resultsData: getSmokeResultsData(),
    });
  }, [navigation]);

  const heroBadgeLabel = resumeCategoryId
    ? (t('resume_last_topic', userLang) || 'Resume Last Topic')
    : (t('daily_challenge_label', userLang) || 'Daily Challenge');
  const heroActionLabel = resumeCategoryId
    ? (t('resume_last_topic', userLang) || 'Resume Last Topic')
    : (t('start_todays_challenge', userLang) || "Start Today's Challenge");
  const heroSupportingText = featuredCategory?.description
    || t('explore_gods_word', userLang)
    || "Explore God's Word";
  const multiplayerHeader = mode === 'multiplayer' ? (
    <MultiplayerSetupHeroCard
      badgeLabel={t('multiplayer_lobby.setupCategoryBadge', userLang)}
      title={t('multiplayer_lobby.setupCategoryTitle', userLang)}
      subtitle={t('multiplayer_lobby.setupCategorySubtitle', userLang).replace('{{groupType}}', groupMeta.groupLabelLower)}
      groupName={groupMeta.groupName}
    />
  ) : undefined;

  return (
    <View style={[tw`flex-1`, styles.shellCanvas]} data-oid="p1pg43x">
      <StatusBar barStyle="light-content" />
      <LinearGradient
        colors={[iqesColors.canvas, iqesColors.navy, '#210E3C']}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.shellOrbTop} pointerEvents="none" />
      <View style={styles.shellOrbBottom} pointerEvents="none" />

      {/* Mode Indicator - Subtle DS label style */}
      {__DEV__ && (
        <View style={{
          position: 'absolute',
          top: 40,
          right: 10,
          backgroundColor: 'rgba(8,10,24,0.82)',
          borderWidth: 1,
          borderColor: 'rgba(246,190,58,0.28)',
          paddingHorizontal: 8,
          paddingVertical: 3,
          borderRadius: 12,
          zIndex: 10,
        }}>
          <DSText variant="micro" color={iqesColors.goldSoft} style={tw`font-medium`}>
            {mode.toUpperCase()}
          </DSText>
        </View>
      )}

      {/* Main Category Selection */}
      <View style={tw`flex-1 w-full h-full justify-start`}>
        <CategoryPicker
          categories={categories}
          loading={loading}
          onCategorySelect={handleCategorySelect}
          header={mode === 'solo' ? (
            <>
              {userId && (
                <DailyVerseCard language={userLang} userId={userId} />
              )}
              <SoloEntryHeroCard
                featuredCategory={featuredCategory}
                onSelect={handleCategorySelect}
                language={userLang}
                streakDays={streakInfo?.current_streak || 0}
                level={xpInfo?.current_level || 1}
                rankLabel={translateRankName(xpInfo?.rank || 'Scholar', userLang)}
                badgeLabel={heroBadgeLabel}
                ctaLabel={heroActionLabel}
                supportingText={heroSupportingText}
              />
              {isGuest && guestProgress && (
                <GuestProgressCard
                  language={userLang}
                  quizzesCompleted={guestProgress.quizzesCompleted}
                  accuracy={guestAccuracy}
                  lastPlayedLabel={guestLastPlayedLabel}
                />
              )}
              {showSmokeLab && (
                <SmokeScenarioPanel
                  onOpenScenario={handleOpenSmokeScenario}
                  onOpenLiveLoop={handleOpenSmokeLiveLoop}
                  onOpenResults={handleOpenSmokeResults}
                />
              )}
            </>
          ) : multiplayerHeader}
          data-oid="unified-category-picker"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  shellCanvas: {
    backgroundColor: iqesColors.canvas,
  },
  shellOrbTop: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: 130,
    right: -100,
    top: -84,
    backgroundColor: 'rgba(139,92,246,0.2)',
  },
  shellOrbBottom: {
    position: 'absolute',
    width: 230,
    height: 230,
    borderRadius: 115,
    left: -120,
    bottom: 42,
    backgroundColor: 'rgba(246,190,58,0.1)',
  },
  shellHeaderWrap: {
    paddingHorizontal: 18,
    paddingTop: 14,
    paddingBottom: 8,
  },
  kingdomHeroPressable: {
    borderRadius: 26,
    overflow: 'hidden',
    borderWidth: 1.3,
    borderColor: 'rgba(246,190,58,0.52)',
    shadowColor: iqesColors.gold,
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.26,
    shadowRadius: 22,
    elevation: 10,
  },
  kingdomHero: {
    minHeight: 286,
    padding: 22,
    justifyContent: 'space-between',
    backgroundColor: iqesColors.canvasRaised,
  },
  kingdomHeroImage: {
    opacity: 0.82,
  },
  heroGlowLarge: {
    position: 'absolute',
    width: 180,
    height: 180,
    borderRadius: 90,
    right: -34,
    top: -38,
    backgroundColor: 'rgba(246,190,58,0.16)',
  },
  heroTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  kingdomTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  kingdomIconBadge: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    backgroundColor: 'rgba(246,190,58,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(246,190,58,0.5)',
  },
  kingdomEyebrow: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 0.8,
  },
  heroXpRing: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 3,
    borderColor: iqesColors.goldBright,
    backgroundColor: 'rgba(8,10,24,0.74)',
  },
  heroXpValue: {
    color: iqesColors.goldSoft,
    fontSize: 24,
    lineHeight: 27,
    fontWeight: '900',
  },
  heroXpLabel: {
    color: iqesColors.ivory,
    fontSize: 11,
    fontWeight: '800',
    opacity: 0.86,
  },
  heroBadgeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 24,
  },
  heroPill: {
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 7,
    backgroundColor: 'rgba(76,29,120,0.8)',
    borderWidth: 1,
    borderColor: 'rgba(216,180,254,0.38)',
  },
  heroPillGold: {
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 7,
    backgroundColor: 'rgba(67,44,15,0.78)',
    borderWidth: 1,
    borderColor: 'rgba(246,190,58,0.58)',
  },
  heroPillText: {
    fontWeight: '900',
    letterSpacing: 0.5,
  },
  heroTitle: {
    marginTop: 18,
    fontSize: 34,
    lineHeight: 39,
    fontWeight: '900',
  },
  heroSubtitle: {
    marginTop: 8,
    fontSize: 15,
    lineHeight: 22,
  },
  heroMetaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 18,
  },
  heroMetaPill: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 18,
    paddingHorizontal: 13,
    paddingVertical: 8,
    backgroundColor: 'rgba(255,255,255,0.09)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
  },
  heroMetaText: {
    marginLeft: 7,
    fontWeight: '900',
  },
  heroActionRow: {
    marginTop: 18,
    alignItems: 'flex-start',
  },
  heroPrimaryCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 22,
    paddingHorizontal: 20,
    paddingVertical: 13,
    backgroundColor: iqesColors.goldSoft,
  },
  heroPrimaryCtaText: {
    fontWeight: '900',
    fontSize: 14,
  },
  shellSectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
    gap: 12,
  },
  shellSectionTitle: {
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 1.1,
  },
  shellSectionRail: {
    height: 1,
    flex: 1,
    backgroundColor: 'rgba(246,190,58,0.32)',
  },
});

export default UnifiedCategoryScreen;
